import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Flag from 'react-world-flags';
import { UPDATE_OWNER_PROFILE } from 'services/user';
import { useMutation } from '@apollo/client';

interface TranslateButtonProps {
  updateOwnerOnChange?: boolean;
}

export const TranslateButton = ({
  updateOwnerOnChange = false,
}: TranslateButtonProps) => {
  const [t, i18n] = useTranslation('navigation');
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [updateOwnerProfile] = useMutation(UPDATE_OWNER_PROFILE);

  const languageOptions = [
    { code: 'en', label: t('en'), flag: 'US' },
    { code: 'es', label: t('es'), flag: 'ES' },
    // Add more languages here if needed
  ];

  const currentLang =
    languageOptions.find((l) => l.code === i18n.language) || languageOptions[0];

  const handleChangeLanguage = async (langCode: string) => {
    i18n.changeLanguage(langCode);
    if (updateOwnerOnChange) {
      try {
        await updateOwnerProfile({
          variables: { data: { language: langCode } },
        });
      } catch (e) {
        // Optionally handle error
        console.error('Failed to update owner language', e);
      }
    }
    if (detailsRef.current) detailsRef.current.open = false;
  };

  return (
    <div className="relative">
      <details
        ref={detailsRef}
        className="dropdown dropdown-top dropdown-end w-fit rounded-full text-center"
      >
        <summary className="btn btn-ghost bg-base-200 btn-sm font-poppins min-h-8 hover:bg-base-100 flex h-8 w-fit cursor-pointer items-center justify-center gap-2 px-3 shadow-sm transition-colors">
          <Flag code={currentLang.flag} className="h-4 w-4" />
          <span className="text-sm font-medium">{currentLang.label}</span>
          <svg className="ml-1 h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.08 1.04l-4.25 4.65a.75.75 0 01-1.08 0l-4.25-4.65a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </summary>
        <ul className="menu dropdown-content rounded-box bg-base-100 border-base-300 absolute bottom-full left-1/2 z-[1001] mb-1 w-36 -translate-x-1/2 border p-2 shadow-lg">
          {languageOptions.map((lang) => (
            <li key={lang.code}>
              <button
                className="font-poppins text-base-content flex w-full items-center gap-2"
                onClick={() => handleChangeLanguage(lang.code)}
              >
                <Flag code={lang.flag} className="h-4 w-4" />
                <span>{lang.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
};
